import {
  KebabHorizontalIcon,
  PencilIcon,
  PlusIcon,
  StackIcon,
  SyncIcon,
  TrashIcon,
} from "@primer/octicons-react";
import {
  ActionList,
  ActionMenu,
  Box,
  useConfirm,
} from "@primer/react";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NGAY_NGHI_API_END_POINT } from "../../../api/ngayNghiApi";
import Button from "../../../components/ui/button";
import DataTable from "../../../components/ui/data-table";
import Text from "../../../components/ui/text";
import { useCommonContext } from "../../../contexts/common";
import { useCommonSelectedHook } from "../../../hooks/useCommonSelectedHook";
import { ePageBaseStatus } from "../../../models/ePageBaseStatus";
import { INgayNghi } from "../../../models/response/category/INgayNghi";
import { actions } from "../../../state/actions/actionsWrapper";
import { RootState } from "../../../state/reducers";
import NgayNghiEditModal from "./NgayNghiEditModal";
import { ITruong } from "../../../models/response/truong/ITruong";
import { truongApi } from "../../../api/truongApi";

const NgayNghiPage = () => {
  const { status, ngayNghis, isShowEditModal } = useSelector(
    (x: RootState) => x.danhMucWrapper.ngayNghi
  );
  const { dm_coso_id } = useCommonSelectedHook();
  const { translate, checkAccesiableTo } = useCommonContext();
  const confirm = useConfirm();
  const dispatch = useDispatch();

  // Phân quyền các chức năng được sử dụng trong trang ca ăn
  const canView = checkAccesiableTo(NGAY_NGHI_API_END_POINT, "GET");
  const canAdd = checkAccesiableTo(NGAY_NGHI_API_END_POINT, "POST");
  const canEdit = checkAccesiableTo(NGAY_NGHI_API_END_POINT, "PUT");
  const canDelete = checkAccesiableTo(`${NGAY_NGHI_API_END_POINT}/{id}`, "DELETE");

  const [dm_truongs, setDmTruongs] = useState<ITruong[]>([]);
  const loadTruongAsync = async () => {
    const res = await truongApi.selectAll();
    if (res.is_success) {
      const filteredData = res.data.filter(
        (truong: ITruong) => truong.dm_coso_id === dm_coso_id
      );
      setDmTruongs(filteredData);
    }
  };

  useEffect(() => {
    if (
      canView &&
      (status === ePageBaseStatus.is_not_initialization ||
        status === ePageBaseStatus.is_need_reload)
    ) {
      handleReload();
      loadTruongAsync();
    }
  }, [status, canView]);
  const filteredData = useMemo(() => {
    if (ngayNghis.length > 0 && dm_coso_id) {
      return ngayNghis.filter(x => x.dm_coso_id === dm_coso_id);
    }
    return ngayNghis;
  }, [dm_coso_id, ngayNghis]);
  const handleReload = () => {
    if (!canView) return;
    dispatch(actions.danhMucWrapper.ngayNghi.LOAD_START(undefined));
  };
  const handleDelete = async (id: number) => {
    if (!canDelete) return;
    if (
      await confirm({
        content: translate("Base.Label.DeleteConfirm.Content"),
        title: translate("Base.Label.Confirm"),
        cancelButtonContent: translate("Base.Label.Close"),
        confirmButtonContent: translate("Base.Label.Delete"),
        confirmButtonType: "danger",
      })
    ) {
      dispatch(actions.danhMucWrapper.ngayNghi.DELETE_START(id));
    }
  };

  // Kiểm tra quyền truy cập trang
  if (!canView) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", p: 4 }}>
        <Text text={translate("Bạn không có quyền truy cập trang này")} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: 3,
      }}
    >
      <DataTable
        height={`${window.innerHeight - 260}px`}
        data={filteredData}
        isLoading={status === ePageBaseStatus.is_loading}
        title={"Danh sách ngày nghỉ"}
        searchEnable
        paging={{
          enable: true,
          pageSizeItems: [20, 50, 200, 500, 1000],
        }}
        actionComponent={
          <Box
            sx={{
              display: "flex",
            }}
          >
            {canView && (
              <Button
                text="Base.Label.Refresh"
                leadingVisual={SyncIcon}
                size="medium"
                sx={{ mx: 1 }}
                onClick={() => {
                  handleReload();
                }}
              />
            )}
            {canAdd && (
              <Button
                text="Base.Label.AddNew"
                leadingVisual={PlusIcon}
                size="medium"
                variant="primary"
                onClick={() => {
                  dispatch(
                    actions.danhMucWrapper.ngayNghi.SHOW_EDIT_MODAL(undefined)
                  );
                }}
              />
            )}
          </Box>
        }
        columns={[
          {
            dataField: "noi_dung",
            caption: translate("Nội dung"),
            cellRender: (data) => (
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Box
                  sx={{
                    display: "inline-flex",
                    verticalAlign: "sub",
                    lineHeight: 1,
                    mr: 1,
                  }}
                >
                  <div style={{ color: "#62825D" }}>
                    <StackIcon size={14} />{" "}
                  </div>
                </Box>

                <span style={{ fontWeight: "500" }}>{data.noi_dung}</span>
              </Box>
            ),
          },
          {
            dataField: "from_date",
            caption: translate("Từ ngày"),
            cellRender: (data: INgayNghi) => {
              return (
                <Box>
                  {data.from_date ? moment(data.from_date).format("DD/MM/YYYY") : ""}
                </Box>
              )
            }
          },
          {
            dataField: "to_date",
            caption: translate("Đến ngày"),
            cellRender: (data: INgayNghi) => {
              return (
                <Box>
                  {data.to_date ? moment(data.to_date).format("DD/MM/YYYY") : ""}
                </Box>
              )
            }
          },
          {
            dataField: "dm_truong_id",
            caption: translate("Trường"),
            cellRender: (data: INgayNghi) => {
              return (
                <Box>
                  {dm_truongs.find(x=>x.id === data.dm_truong_id)?.ten_truong}
                </Box>
              )
            }
          },
          ...(canEdit || canDelete ? [{
            id: "cmd",
            caption: "",
            width: "50px",
            align: "center" as const,
            cellRender: (data: INgayNghi) => {
              return (
                <ActionMenu>
                  <ActionMenu.Button
                    icon={KebabHorizontalIcon}
                    variant="invisible"
                    sx={{ m: -1 }}
                  >
                    &nbsp;
                  </ActionMenu.Button>
                  <ActionMenu.Overlay width="auto">
                    <ActionList>
                      {canEdit && (
                        <ActionList.Item
                          onSelect={() => {
                            dispatch(
                              actions.danhMucWrapper.ngayNghi.SHOW_EDIT_MODAL(data)
                            );
                          }}
                        >
                          <ActionList.LeadingVisual>
                            <PencilIcon />
                          </ActionList.LeadingVisual>
                          <Text text="Base.Label.Edit" />
                        </ActionList.Item>
                      )}
                      {canDelete && (
                        <ActionList.Item
                          variant="danger"
                          onSelect={() => {
                            handleDelete(data.id);
                          }}
                        >
                          <ActionList.LeadingVisual>
                            <TrashIcon />
                          </ActionList.LeadingVisual>
                          <Text text="Base.Label.Delete" />
                        </ActionList.Item>
                      )}
                    </ActionList>
                  </ActionMenu.Overlay>
                </ActionMenu>
              );
            },
          }] : []),
        ]}
      />
      {isShowEditModal && <NgayNghiEditModal />}
    </Box>
  );
};

export default NgayNghiPage;
